import queryString from 'query-string';
import {camelizeKeys} from 'humps';
import TokenManager from '@setapp/auth-tokens-manager';
import Authenticator from '@setapp/customer-authenticator';
import UiElement from '../../../shared/ui-element/ui-element';
import config from '../../../shared/config/config';
import analytics, {events} from '../../../shared/analytics';
import request from '../../utils/request/request';
import SignupFormStepEmail from './signup-form-step-email';
import SignupFormStepPassword from './signup-form-step-password';
import SignupFormStepName from './signup-form-step-name';

import './signup-form.scss';

const REGISTRATION_FLOW_SINGLE_APP = 'appLanding';
const REGISTRATION_FLOW_BUNDLE = 'bundleLanding';
const DEFAULT_TIER_TYPE = 'regular_monthly';

/*
* Redirect timeout for analytics
* */
const REDIRECT_TIMEOUT = 300;
const STEP_ANIMATION_TIMEOUT = 150;

class SignupFormManager extends UiElement {
  ui = {
    socialButtonsBlock: '.js-social-buttons',
  };

  uiCollections = {
    socialSignupButtons: '.js-social-signup-button',
    formSteps: '.js-signup-form-step',
  };

  events = {
    'click @uiCollections.socialSignupButtons': 'handleSocialButtonClick',
  };

  signupParams = {};

  signupFormStepNames = {
    email: 'email',
    password: 'password',
    name: 'name',
  };

  constructor() {
    super({rootElement: '#signup-form-manager'});

    const cookiesDomain = config.get('cookiesDomain');
    const tokenManager = new TokenManager({
      cookiesDomain,
      secureCookies: window.location.protocol === 'https:',
      rememberPeriod: 365,
      accessTokenName: 'customer_access_token',
      refreshTokenName: 'customer_refresh_token',
    });

    this.authenticator = new Authenticator({
      apiRoot: config.get('customerAccountAPI.root'),
      cookiesDomain,
      tokenManager,
    });

    this.signupFormStepEmail = new SignupFormStepEmail({
      onSubmit: this.handleEmailFormSubmit,
    });
    this.signupFormStepPassword = new SignupFormStepPassword({
      onSubmit: this.handlePasswordFormSubmit,
      onBackButtonClick: this.handleBackButtonStepPasswordClick,
      onRetypeButtonClick: this.handleRetypeButtonClick,
    });
    this.signupFormStepName = new SignupFormStepName({
      onSubmit: this.handleUsernameFormSubmit,
      onBackButtonClick: this.handleBackButtonStepNameClick,
      onRetypeButtonClick: this.handleRetypeButtonClick,
    });
  }

  onInit({signupParams = {}, signupHideSocials, campaign} = {}) {
    super.onInit();
    this.signupFormStepEmail.init();
    this.signupFormStepPassword.init();
    this.signupFormStepName.init();

    const registrationFlow = this.getRegistrationFlow();
    if (registrationFlow) {
      this.signupParams = {
        registration_flow: registrationFlow,
        ...this.signupParams,
      };
    }

    if (campaign) {
      this.signupParams = {
        ...this.signupParams,
        campaign,
      };
    }

    this.signupParams = {
      ...this.signupParams,
      ...signupParams,
      /**
       * always check for a tier_type and set a default value if none passed to prevent form from sending tier_type
       * taken from the previous opening
       */
      tier_type: signupParams.tier_type || DEFAULT_TIER_TYPE,
    };

    if (signupHideSocials) {
      this.hideSocialButtons();
    }

    this.updateSocialSignupParams(this.signupParams);

    analytics.trackEvent(events.SIGNUP_FORM_OPEN);
  }

  destroy() {
    super.destroy();

    this.showSocialButtons();
  }

  getSignupData() {
    // TODO: support ab tests segments
    return {
      // merge manually passed params first to prevent form values overwriting
      ...this.signupParams,
    };
  }

  updateSignupParams(newParams) {
    this.signupParams = {
      ...this.signupParams,
      ...newParams,
    };
  }

  showSignupFormStep = (stepName) => {
    this.currentStep = stepName;
    this.rootElement.classList.toggle('signup-form-step_faded');

    setTimeout(() => {
      this.uiCollections.formSteps.forEach((step) => {
        if (step.dataset.stepName === stepName) {
          step.classList.remove('d-none');
        } else {
          step.classList.add('d-none');
        }
      });
      this.rootElement.classList.toggle('signup-form-step_faded');
    }, STEP_ANIMATION_TIMEOUT);
  };

  updateEmailPlaceholders(email) {
    this.signupFormStepPassword.updateEmailPlaceholder(email);
    this.signupFormStepName.updateEmailPlaceholder(email);
  }

  handleSocialButtonClick (e) {
    e.preventDefault();
    const socialAnalyticEvent = e.target.dataset.analyticEventAction;

    analytics.trackEvent(events[socialAnalyticEvent]);
    setTimeout(() => {
      window.location.assign(e.target.href);
    }, REDIRECT_TIMEOUT);
  }

  handleBackButtonStepPasswordClick = () => {
    this.showSignupFormStep(this.signupFormStepNames.email);
  };

  handleBackButtonStepNameClick = () => {
    this.showSignupFormStep(this.signupFormStepNames.password);
  };

  handleRetypeButtonClick = () => {
    this.showSignupFormStep(this.signupFormStepNames.email);
  };

  handleEmailFormSubmit = (data) => {
    this.updateSignupParams({email: data.email});
    this.updateEmailPlaceholders(data.email);
    this.showSignupFormStep(this.signupFormStepNames.password);
  };

  handlePasswordFormSubmit = (data) => {
    this.updateSignupParams({password: data.password});
    this.showSignupFormStep(this.signupFormStepNames.name);
  };

  handleUsernameFormSubmit = (data) => {
    this.updateSignupParams({
      name: data.name,
      marketing_subscribed: data.marketing_subscribed,
      terms_accepted: data.terms_accepted,
    });
    this.submitSignup();
  };

  submitSignup() {
    const data = this.getSignupData();
    const locale = this.rootElement.dataset.language;

    return this.authenticator.signUp(data, {language: locale})
      .then(() => this.handleSignUpSuccess(data))
      .catch(this.handleSignUpError);
  }

  handleSignUpSuccess = signUpData => this.fetchUserProfile()
    .then((user) => {
      analytics.trackEvent({
        ...events.SIGN_UP_SUCCESS,
        userEmail: user.email,
        userId: user.id,
      });

      if (signUpData.marketing_subscribed) {
        analytics.trackEvent(events.SIGN_UP_EMAIL_OPT_IN);
      }

      setTimeout(() => {
        if (this.shouldRedirectToCustomerAccount(user)) {
          window.location.assign(config.get('customerAccountPages.successfulRegistration'));
        } else {
          window.location.assign(this.rootElement.dataset.nextPage);
        }
      }, REDIRECT_TIMEOUT);
    });

  handleSignUpError = (errors) => {
    this.signupFormStepName.enableSubmitBtn();

    errors.getAll().forEach((error) => {
      const errorMessage = error.message || this.rootElement.dataset.defaultError;

      if (error.field === 'email') {
        this.signupFormStepEmail.showEmailFieldError(errorMessage);
        this.showSignupFormStep('email');
      } else if (error.field === 'name') {
        this.signupFormStepName.showFieldError('name', errorMessage);
      }
    });
  };

  fetchUserProfile() {
    return request.get(config.get('customerAccountAPI.account'));
  }

  shouldRedirectToCustomerAccount(user) {
    const isSingleAppRegistrationFlow = user.registration_flow === REGISTRATION_FLOW_SINGLE_APP;
    const isBundleRegistrationFlow = user.registration_flow === REGISTRATION_FLOW_BUNDLE;

    return user.payment_info_required || isSingleAppRegistrationFlow || isBundleRegistrationFlow;
  }

  hideSocialButtons() {
    this.ui.socialButtonsBlock.classList.add('d-none');
  }

  showSocialButtons() {
    this.ui.socialButtonsBlock.classList.remove('d-none');
  }

  updateSocialSignupParams(params) {
    this.uiCollections.socialSignupButtons.forEach((element) => {
      const {signupParams = null, ...restQuery} = queryString.parse(element.search);

      // eslint-disable-next-line no-param-reassign
      element.search = queryString.stringify({
        ...restQuery,
        signupParams: JSON.stringify({
          ...JSON.parse(signupParams),
          ...camelizeKeys(params),
        }),
      });
    });
  }

  getRegistrationFlow() {
    return JSON.parse(this.rootElement.dataset.registrationFlow || null);
  }
}

export default SignupFormManager;
